import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { Thing, WithContext } from 'schema-dts';

import { hygraphClient } from '@/lib/graphql-clients';
import {
  FaqPage as HygraphFaqPage,
  FaqPageWhereInput,
  getSdk,
  Maybe,
  PageSidebar,
} from '@/lib/types';
import usePageLogging from '@/hooks/use-page-logging';

import { linkedSectionID } from '@/components/template/linked-section';
import { TemplateBreadcrumbItem } from '@/components/template/template-breadcrumb';
import TemplateRenderer, {
  TemplateDetailRendererProps,
} from '@/components/template/template-renderer';
import { TableOfContentsItem } from '@/components/template/template-table-of-contents';

import { GlobalContextProvider } from '@/context/global-context';
import FaqPageContextProvider from '@/features/faq-page/context/faq-page-context';
import faqPageTransformer from '@/features/faq-page/hygraph/transformers/faq-page.transformer';
import FaqPageListingService from '@/features/faq-page/services/faq-page-listing.service';
import { FaqPage } from '@/features/faq-page/type';
import { FaqPageStaticDataProps } from '@/features/faq-page/type';
import seoTransformer from '@/hygraph/transformers/seo.transformer';
import templateDetailTransformer from '@/hygraph/transformers/template-details.transformer';
import { HomeRouterHandleType } from '@/pages/[...slug]';
import { RouteHandlerProps } from '@/router/types.router';
import RegionsService from '@/services/regions.service';
import SchemaService from '@/services/schema-service';

type FaqPageProps = {
  faqPage: FaqPage;
  schema: WithContext<Thing>[];
  preview?: boolean;
  faqPageStaticData: FaqPageStaticDataProps;
};

const FaqPageComponent: NextPage<FaqPageProps> = ({
  faqPage,
  schema,
  faqPageStaticData,
}) => {
  usePageLogging(HomeRouterHandleType.FaqPage);

  const { title } = faqPage;
  const sidebarComponents = faqPage.sidebar?.components || [];

  const breadcrumbs = faqBreadcrumbTransformer(faqPage);
  const toc = faqTocTransformer(faqPage.mainContent);

  const heading = faqPage.templateDetails?.heading || faqPage.title;
  const publishedAt = faqPage.templateDetails?.published || faqPage.publishedAt;
  const updatedAt = faqPage.templateDetails?.modified || faqPage.modifiedAt;

  // this shouldn't be, but MAYBE???
  if (!faqPage.templateDetails) return <></>;

  const templateDetails: TemplateDetailRendererProps =
    templateDetailTransformer(faqPage.templateDetails);

  // todo: map components to some kind of type

  templateDetails.title = heading;
  templateDetails.publishedAt = publishedAt;
  templateDetails.modifiedAt = updatedAt;
  templateDetails.components = faqPage.mainContent;
  templateDetails.tocItems = toc;
  templateDetails.breadcrumbs = breadcrumbs;
  templateDetails.sidebarComponents = sidebarComponents;

  return (
    <GlobalContextProvider
      pageMeta={seoTransformer(templateDetails?.seo, { title })}
      schema={schema}
    >
      <FaqPageContextProvider
        specialty={faqPage.specialty}
        discipline={faqPage.discipline}
        state={faqPage.state}
        categories={faqPage.categories}
        faqPageStaticData={faqPageStaticData}
      >
        <TemplateRenderer {...templateDetails} />
      </FaqPageContextProvider>
    </GlobalContextProvider>
  );
};

const faqBreadcrumbTransformer = (
  ancestors: Maybe<FaqPage>
): TemplateBreadcrumbItem[] => {
  const breadcrumbs: TemplateBreadcrumbItem[] = [];

  // loop that gets the slug and title of ancestors.parent and pushes it to the breadcrumbs array and then sets the current ancestor to the parent of the current ancestor

  let currentAncestor: Maybe<FaqPage> | undefined = ancestors?.parent;
  while (currentAncestor) {
    breadcrumbs.push({
      path: '/' + currentAncestor.slug,
      title: currentAncestor.title || '',
    });

    for (let i = breadcrumbs.length - 1; i >= 0; i--) {
      if (breadcrumbs[i - 1]) {
        breadcrumbs[i - 1].path =
          '/' + currentAncestor.slug + breadcrumbs[i - 1].path;
      }
    }

    currentAncestor = currentAncestor.parent;
  }

  breadcrumbs.push({
    path: '/',
    title: 'Nomad Health',
  });

  breadcrumbs.reverse();
  breadcrumbs.push({ path: '#', title: ancestors?.title || '' });
  return breadcrumbs;
};

const faqTocTransformer = (
  components: FaqPage['mainContent']
): TableOfContentsItem[] => {
  const tocItems: TableOfContentsItem[] = [];
  components.forEach((component) => {
    if (
      'tableOfContents' in component &&
      component.tableOfContents?.enabled &&
      component.tableOfContents?.title
    ) {
      const title = component.tableOfContents?.title;
      const url = `#${linkedSectionID(component)}`;

      tocItems.push({
        title: title,
        url: url,
      });
    }
  });
  return tocItems;
};

const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug;
  const slugs = Array.isArray(slug) ? slug : [slug];
  if (!slugs || slugs[0] !== 'faqs') {
    return { notFound: true };
  }

  const sdk = getSdk(hygraphClient(context.preview));

  let filters: FaqPageWhereInput | null = null;

  if (slugs.length > 1) {
    slugs.forEach((slug) => {
      filters = {
        slug,
        parent: filters,
      };
    });
  } else {
    filters = { slug: slugs[0] };
  }

  const faqPagesPromise = sdk.FaqPages({
    filters,
  });
  const defaultFaqSidebarPromise = sdk.Sidebars({
    where: { slug: 'default-faq' },
  });

  const [{ faqPages }, { pageSidebars }] = await Promise.all([
    faqPagesPromise,
    defaultFaqSidebarPromise,
  ]);

  const hygraphFaqPage = faqPages.at(-1) as HygraphFaqPage | null;
  if (!hygraphFaqPage) return { notFound: true };

  const faqPage = faqPageTransformer(hygraphFaqPage);

  const defaultSidebar = (pageSidebars.at(-1) || null) as PageSidebar | null;

  faqPage.sidebar ||= defaultSidebar;
  const jsonFaqPage = JSON.parse(JSON.stringify(faqPage));

  const [regions, faqPageListings] = await Promise.all([
    RegionsService.get(),
    FaqPageListingService.get(faqPage),
  ]);

  const faqPageStaticData: FaqPageStaticDataProps = {
    searchAdventures: {},
    relatedLinks: {},
    jobBlocks: {},
    regions,
    faqPageListings,
  };

  return {
    props: {
      slug: slugs,
      faqPage: jsonFaqPage,
      faqPageStaticData,
      preview: context.preview || false,
      schema: buildFaqPageSchema(faqPage),
    },
    revalidate: 60, // 1 minute
  };
};

const getStaticPaths: GetStaticPaths = async () => {
  const sdk = getSdk(hygraphClient());
  const { faqPages } = await sdk.FaqPages();
  const paths: { params: { slug: string[] } }[] = [];

  faqPages.forEach((faqPage) => {
    if (!faqPage.slug) return;

    const slugs: string[] = [faqPage.slug];
    let currentParent = faqPage.parent;
    while (currentParent && currentParent.slug) {
      slugs.unshift(currentParent.slug);
      currentParent = currentParent.parent;
    }
    paths.push({ params: { slug: slugs } });
  });

  return { paths, fallback: 'blocking' };
};

function buildFaqPageSchema(page: FaqPage): WithContext<Thing>[] {
  if (page.templateDetails && page.templateDetails?.seo?.noIndex) return [];

  const { images, questions, things } = SchemaService.buildSchemaData(
    page.mainContent
  );
  const pageSchema = SchemaService.buildPageThing(page, images);

  if (questions.length > 0) {
    things.unshift({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: questions,
    });
  }

  const breadcrumbs = faqBreadcrumbTransformer(page);
  if (breadcrumbs.length > 0) {
    things.unshift(SchemaService.buildBreadcrumbsList(breadcrumbs));
  }

  return [{ '@context': 'https://schema.org', ...pageSchema }, ...things];
}

const FaqPageHandler: RouteHandlerProps = {
  getStaticPaths,
  getStaticProps,
  Component: FaqPageComponent,
};

export default FaqPageHandler;
